import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Cell, Pie, PieChart } from "recharts";
import {
  FaBell,
  FaCalendarCheck,
  FaClipboardList,
  FaGraduationCap,
  FaTrophy,
  FaUserGraduate,
  FaWallet,
} from "react-icons/fa";
import { ApiUrl } from "../../ApiUrl";

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getAttendanceMonthValue = (date) =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}`;

const getAttendanceDateRange = (monthValue) => {
  const [year, month] = monthValue.split("-").map(Number);
  const startDate = new Date(year, month - 1, 1);
  const today = new Date();
  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth() + 1;
  const endDate = isCurrentMonth
    ? today
    : new Date(year, month, 0);

  return {
    fromDate: formatDateForApi(startDate),
    toDate: formatDateForApi(endDate),
  };
};

const getAttendanceMonthOptions = () => {
  const today = new Date();
  return Array.from({ length: 12 }, (_, index) => {
    const monthDate = new Date(today.getFullYear(), index, 1);
    return {
      value: getAttendanceMonthValue(monthDate),
      label: monthDate.toLocaleDateString("en-US", { month: "long" }),
      disabled: index > today.getMonth(),
    };
  });
};

const formatDisplayDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-GB");
};

const getSafeJson = async (url) => {
  const response = await fetch(url);
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : null;

  if (!response.ok || !payload || payload.message !== "Success") {
    throw new Error(payload?.message || "Unable to load data.");
  }

  return payload;
};

const panelStyle = {
  background: "#dce8ff",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 6px 18px rgba(30, 64, 175, 0.12)",
  border: "1px solid rgba(59, 130, 246, 0.12)",
};

const panelHeaderStyle = {
  background: "linear-gradient(90deg, #1f7ae0 0%, #1560b8 100%)",
  color: "#ffffff",
  padding: "10px 14px",
  fontSize: "15px",
  fontWeight: "700",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const softCardStyle = {
  background: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
};

const emptyBoxStyle = {
  background: "#ffffff",
  borderRadius: "14px",
  padding: "20px",
  color: "#64748b",
  minHeight: "110px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
};

function Dashboards() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceError, setAttendanceError] = useState("");
  const [selectedAttendanceMonth, setSelectedAttendanceMonth] = useState(
    getAttendanceMonthValue(new Date())
  );
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    mentor: null,
    attendance: {
      total_present: 0,
      total_absent: 0,
      total_classes: 0,
      attendance_percentage: 0,
    },
    assignments: [],
    circulars: [],
    library: [],
    results: [],
    feeDue: 0,
    hostel: {
      assigned: false,
      data: [],
    },
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      const studentId = sessionStorage.getItem("userId");
      const academicYearId = localStorage.getItem("academicSessionId") || "";
      const orgId = localStorage.getItem("orgId") || "";
      const branchId = localStorage.getItem("branchId") || "";

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      const commonQueryParams = new URLSearchParams();
      if (academicYearId) commonQueryParams.append("academic_year_id", academicYearId);
      if (orgId) commonQueryParams.append("org_id", orgId);
      if (branchId) commonQueryParams.append("branch_id", branchId);

      try {
        setLoading(true);
        setError("");

        const [
          profile,
          mentor,
          assignmentsPayload,
          circularsPayload,
          libraryPayload,
          resultsPayload,
          feeDuePayload,
          hostelPayload,
        ] = await Promise.all([
          getSafeJson(
            `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentProfile/${studentId}/?academic_year_id=${academicYearId}`
          ).then((payload) => payload.data).catch(() => null),
          getSafeJson(
            `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentMentor/${studentId}/?${commonQueryParams.toString()}`
          ).then((payload) => payload.data).catch(() => null),
          getSafeJson(
            `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentAssignments/${studentId}/?${commonQueryParams.toString()}`
          ).then((payload) => (Array.isArray(payload.data) ? payload.data : [])).catch(() => []),
          getSafeJson(
            `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentCircularNotifications/${studentId}/?${commonQueryParams.toString()}`
          ).then((payload) => (Array.isArray(payload.data) ? payload.data : [])).catch(() => []),
          getSafeJson(
            `${ApiUrl.apiurl}STUDENT_LIBRARY/GetStudentLibraryDetails/${studentId}/?${commonQueryParams.toString()}`
          ).then((payload) => (Array.isArray(payload.data) ? payload.data : [])).catch(() => []),
          getSafeJson(
            `${ApiUrl.apiurl}reportcard/studentExamResult/${studentId}/?${commonQueryParams.toString()}`
          ).then((payload) => (Array.isArray(payload.data) ? payload.data : [])).catch(() => []),
          fetch(
            `${ApiUrl.apiurl}FEE_LEDGER/GetFEELEDGERBASEDONCONDITION/?academic_year_id=${academicYearId}&studentId=${studentId}&showbalancefees=true`
          )
            .then(async (response) => {
              const contentType = response.headers.get("content-type") || "";
              const payload = contentType.includes("application/json")
                ? await response.json()
                : null;

              if (!response.ok || !payload || payload.message !== "success!!") {
                return 0;
              }

              const feeRow = Array.isArray(payload.data) && payload.data.length > 0
                ? payload.data[0]
                : null;

              return Number(feeRow?.remaining_fees || 0);
            })
            .catch(() => 0),
          fetch(
            `${ApiUrl.apiurl}STUDENT_HOSTEL/GetStudentHostelSummary/${studentId}?${commonQueryParams.toString()}`
          )
            .then(async (response) => {
              const contentType = response.headers.get("content-type") || "";
              const payload = contentType.includes("application/json")
                ? await response.json()
                : null;

              if (!response.ok || !payload || payload.message !== "Success") {
                return { assigned: false, data: [] };
              }

              return {
                assigned: Boolean(payload.assigned),
                data: Array.isArray(payload.data) ? payload.data : [],
              };
            })
            .catch(() => ({ assigned: false, data: [] })),
        ]);

        const resultFallback =
          Array.isArray(resultsPayload) && resultsPayload.length > 0 ? resultsPayload[0] : null;
        const hostelFallback =
          hostelPayload.assigned &&
          Array.isArray(hostelPayload.data) &&
          hostelPayload.data.length > 0
            ? hostelPayload.data[0]
            : null;
        const resolvedProfile = profile || {
          studentname: resultFallback?.student_name || hostelFallback?.studentname || "Student",
          admission_no:
            resultFallback?.admission_no || hostelFallback?.school_admission_no || "-",
          registration_no: resultFallback?.admission_no || hostelFallback?.school_admission_no || "-",
          classname: resultFallback?.course || hostelFallback?.className || "-",
          sectionname: resultFallback?.section || hostelFallback?.sectionName || "-",
          rollno: resultFallback?.roll_no || "-",
          academic_year: resultFallback?.session || academicYearId || "-",
          dob: resultFallback?.dob || hostelFallback?.dob || null,
          profile_pic: null,
        };

        setDashboardData((previous) => ({
          ...previous,
          profile: resolvedProfile,
          mentor,
          // Attendance is loaded independently and must not be reset when
          // the slower general dashboard request completes.
          assignments: assignmentsPayload.slice(0, 3),
          // Keep all notifications so the header count is accurate.
          circulars: circularsPayload,
          library: libraryPayload,
          results: resultsPayload,
          feeDue: feeDuePayload,
          hostel: hostelPayload,
        }));
      } catch (fetchError) {
        console.error("Error loading student dashboard:", fetchError);
        setError(fetchError.message || "Unable to load student dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    const fetchAttendance = async () => {
      const studentId = sessionStorage.getItem("userId");
      const academicYearId = localStorage.getItem("academicSessionId") || "";

      if (!studentId) {
        return;
      }

      const { fromDate, toDate } = getAttendanceDateRange(selectedAttendanceMonth);
      const attendanceQueryParams = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
      });
      if (academicYearId) {
        attendanceQueryParams.append("academic_year_id", academicYearId);
      }

      try {
        setAttendanceLoading(true);
        setAttendanceError("");
        const payload = await getSafeJson(
          `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentAttendance/${studentId}/?${attendanceQueryParams.toString()}`
        );
        setDashboardData((previous) => ({
          ...previous,
          attendance: payload.data?.summary || {
            total_present: 0,
            total_absent: 0,
            total_classes: 0,
            attendance_percentage: 0,
          },
        }));
      } catch (attendanceFetchError) {
        console.error("Error loading student attendance:", attendanceFetchError);
        setDashboardData((previous) => ({
          ...previous,
          attendance: {
            total_present: 0,
            total_absent: 0,
            total_classes: 0,
            attendance_percentage: 0,
          },
        }));
        setAttendanceError("Unable to load attendance for this month.");
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedAttendanceMonth]);

  const { profile, attendance, circulars, results, feeDue } =
    dashboardData;

  const totalCirculars = circulars.length;
  const attendanceMonthOptions = getAttendanceMonthOptions();
  const attendanceHasData = Number(attendance.total_classes || 0) > 0;
  const attendancePercentage = Number(attendance.attendance_percentage || 0);
  const attendanceChartData = [
    ...(attendanceHasData
      ? [
          {
            name: "Present",
            value: Number(attendance.total_present || 0),
            color: "#22a447",
          },
          {
            name: "Absent",
            value: Number(attendance.total_absent || 0),
            color: "#dc3545",
          },
        ]
      : [{ name: "No attendance", value: 1, color: "#dbe2ea" }]),
  ];

  const avatarInitials = (profile?.studentname || "S")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #edf4ff 0%, #dbe8fb 100%)",
      }}
    >
      <Container fluid style={{ width: "98%" }}>
        {error ? <div className="alert alert-danger">{error}</div> : null}

        {loading ? (
          <div
            style={{ ...softCardStyle, padding: "50px", textAlign: "center" }}
          >
            Loading dashboard...
          </div>
        ) : (
          <>
            <Row className="g-3 mb-4">
              <Col lg={4}>
                <div
                  style={{
                    ...softCardStyle,
                    background: "#dce8ff",
                    padding: "14px 16px",
                    minHeight: "92px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "12px",
                        background: "#d9f0dc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1f9d55",
                        fontSize: "22px",
                      }}
                    >
                      <FaCalendarCheck />
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        Attendance
                      </div>
                      <div
                        style={{
                          color: "#16a34a",
                          fontSize: "22px",
                          fontWeight: "800",
                        }}
                      >
                        {attendance.attendance_percentage}%
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={4}>
                <div
                  style={{
                    ...softCardStyle,
                    background: "#dce8ff",
                    padding: "14px 16px",
                    minHeight: "92px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "12px",
                        background: "#e1f0d9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#28a745",
                        fontSize: "20px",
                      }}
                    >
                      <FaWallet />
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        Fee Status
                      </div>
                      <div
                        style={{
                          color: feeDue > 0 ? "#ef4444" : "#16a34a",
                          fontSize: "22px",
                          fontWeight: "800",
                        }}
                      >
                        {feeDue > 0 ? `Rs. ${feeDue}` : "Paid"}
                      </div>
                      <div
                        style={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          marginTop: "2px",
                        }}
                      >
                        {feeDue > 0 ? "Due pending" : "All Clear"}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={4}>
                <div
                  style={{
                    ...softCardStyle,
                    background: "#dce8ff",
                    padding: "14px 16px",
                    minHeight: "92px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "12px",
                        background: "#e6f0ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1677ff",
                        fontSize: "20px",
                      }}
                    >
                      <FaBell />
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        Notifications
                      </div>
                      <div
                        style={{
                          color: "#1677ff",
                          fontSize: "22px",
                          fontWeight: "800",
                        }}
                      >
                        {totalCirculars}
                      </div>
                      <div
                        style={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          marginTop: "2px",
                        }}
                      >
                        New
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ ...panelStyle, marginBottom: "22px" }}>
              <div style={panelHeaderStyle}>
                <span className="d-flex align-items-center gap-2">
                  <FaUserGraduate />
                  Student Information
                </span>
              </div>
              <div style={{ padding: "12px 18px 16px" }}>
                <Row className="align-items-center g-4">
                  <Col
                    lg={3}
                    className="d-flex justify-content-center justify-content-lg-start"
                  >
                    {profile?.profile_pic ? (
                      <img
                        src={profile.profile_pic}
                        alt={profile.studentname || "Student"}
                        style={{
                          width: "118px",
                          height: "118px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "4px solid #1677ff",
                          boxShadow: "0 10px 24px rgba(30, 64, 175, 0.16)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "118px",
                          height: "118px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #1f7ae0 0%, #65a4ff 100%)",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "38px",
                          fontWeight: "800",
                          border: "4px solid #ffffff",
                          boxShadow: "0 10px 24px rgba(30, 64, 175, 0.16)",
                        }}
                      >
                        {avatarInitials}
                      </div>
                    )}
                  </Col>
                  <Col lg={9}>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>Name:</strong> {profile?.studentname || "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Admission No:</strong>{" "}
                          {profile?.admission_no || "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Registration No:</strong>{" "}
                          {profile?.registration_no || "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Email:</strong> {profile?.email || "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Date of Birth:</strong>{" "}
                          {formatDisplayDate(profile?.dob)}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>Class:</strong> {profile?.classname || "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Section:</strong>{" "}
                          {profile?.sectionname || "-"}
                        </div>
                        {/* <div className="mb-2">
                          <strong>Academic Year:</strong>{" "}
                          {profile?.academic_year || "-"}
                        </div> */}
                        {/* <div className="mb-2"><strong>Semester:</strong> Default Semester 1</div>
                        <div className="mb-2"><strong>Status:</strong> <span style={{ color: "#16a34a", fontWeight: 700 }}>ACTIVE</span></div> */}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </div>

            <div style={{ ...panelStyle, marginBottom: "22px" }}>
              <div style={panelHeaderStyle}>
                <span className="d-flex align-items-center gap-2">
                  <FaClipboardList />
                  Student Attendance
                </span>
                <Link to="/student/view-attendance">
                  <Button
                    size="sm"
                    variant="outline-light"
                    style={{ fontWeight: "700" }}
                  >
                    View Details
                  </Button>
                </Link>
              </div>
              <div style={{ padding: "16px 18px 20px" }}>
                <Row className="align-items-end mb-4">
                  <Col md={4} lg={3}>
                    <label
                      htmlFor="student-attendance-month"
                      style={{
                        display: "block",
                        color: "#1e293b",
                        fontWeight: "700",
                        marginBottom: "6px",
                      }}
                    >
                      Attendance Month
                    </label>
                    <select
                      id="student-attendance-month"
                      value={selectedAttendanceMonth}
                      onChange={(event) => setSelectedAttendanceMonth(event.target.value)}
                      style={{
                        width: "100%",
                        minHeight: "42px",
                        padding: "8px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        background: "#ffffff",
                        color: "#334155",
                        fontWeight: "600",
                      }}
                    >
                      {attendanceMonthOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          style={option.disabled ? { color: "#94a3b8" } : undefined}
                        >
                          {option.label} {new Date().getFullYear()}
                          {option.disabled ? " (Not available)" : ""}
                        </option>
                      ))}
                    </select>
                  </Col>
                </Row>

                {attendanceLoading ? (
                  <div style={{ ...emptyBoxStyle, minHeight: "220px" }}>
                    Loading attendance...
                  </div>
                ) : (
                  <>
                    <Row className="g-3 mb-4">
                      {[
                        {
                          label: "Total Days",
                          value: attendance.total_classes,
                          color: "#1677ff",
                          background: "#eef6ff",
                        },
                        {
                          label: "Present",
                          value: attendance.total_present,
                          color: "#22a447",
                          background: "#e9f9ee",
                        },
                        {
                          label: "Absent",
                          value: attendance.total_absent,
                          color: "#dc3545",
                          background: "#fff0f0",
                        },
                      ].map((stat) => (
                        <Col md={4} key={stat.label}>
                          <div
                            style={{
                              ...softCardStyle,
                              background: stat.background,
                              padding: "16px",
                              textAlign: "center",
                            }}
                          >
                            <div style={{ color: "#475569", fontSize: "14px" }}>
                              {stat.label}
                            </div>
                            <div
                              style={{
                                fontSize: "32px",
                                lineHeight: 1.2,
                                fontWeight: "800",
                                color: stat.color,
                                marginTop: "6px",
                              }}
                            >
                              {stat.value || 0}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    <Row className="align-items-center">
                      <Col md={7} className="text-center">
                        <div
                          style={{
                            position: "relative",
                            width: "min(100%, 330px)",
                            height: "280px",
                            margin: "0 auto",
                          }}
                        >
                          <PieChart width={330} height={280}>
                            <Pie
                              data={attendanceChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={102}
                              innerRadius={68}
                              paddingAngle={attendanceHasData ? 2 : 0}
                              labelLine={false}
                              label={attendanceHasData ? ({ value }) => value : false}
                            >
                              {attendanceChartData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              fontSize: "28px",
                              fontWeight: "800",
                              color: "#0f172a",
                            }}
                          >
                            {attendancePercentage}%
                          </div>
                        </div>
                        <div className="d-flex justify-content-center gap-4">
                          <span style={{ color: "#22a447", fontWeight: "700" }}>
                            <span style={{ marginRight: "6px" }}>●</span>
                            Present: {attendanceHasData
                              ? `${((Number(attendance.total_present || 0) / Number(attendance.total_classes || 1)) * 100).toFixed(1)}%`
                              : "0.0%"}
                          </span>
                          <span style={{ color: "#dc3545", fontWeight: "700" }}>
                            <span style={{ marginRight: "6px" }}>●</span>
                            Absent: {attendanceHasData
                              ? `${((Number(attendance.total_absent || 0) / Number(attendance.total_classes || 1)) * 100).toFixed(1)}%`
                              : "0.0%"}
                          </span>
                        </div>
                      </Col>
                      <Col md={5}>
                        <div
                          style={{
                            ...softCardStyle,
                            padding: "22px 20px",
                            background: "#ffffff",
                          }}
                        >
                          <div style={{ color: "#475569", fontSize: "14px" }}>
                            Attendance Percentage
                          </div>
                          <div
                            style={{
                              color: "#1677ff",
                              fontSize: "42px",
                              fontWeight: "800",
                              margin: "4px 0 12px",
                            }}
                          >
                            {attendancePercentage}%
                          </div>
                          <div style={{ color: "#64748b", fontSize: "14px" }}>
                            Based on {attendance.total_classes || 0} recorded day
                            {Number(attendance.total_classes || 0) === 1 ? "" : "s"} in the selected month.
                          </div>
                        </div>
                      </Col>
                    </Row>
                    {attendanceError ? (
                      <div className="text-center mt-3" style={{ color: "#b91c1c" }}>
                        {attendanceError}
                      </div>
                    ) : null}
                    {!attendanceHasData && !attendanceError ? (
                      <div className="text-center mt-3" style={{ color: "#64748b" }}>
                        No attendance records found for the selected month.
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <Row className="g-4">
              <Col lg={6}>
                <div style={panelStyle}>
                  <div style={panelHeaderStyle}>
                    <span className="d-flex align-items-center gap-2">
                      <FaBell />
                      Notifications
                    </span>
                    <span
                      style={{
                        background: "#dc3545",
                        color: "#ffffff",
                        minWidth: "28px",
                        height: "28px",
                        padding: "0 8px",
                        borderRadius: "8px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "700",
                      }}
                      aria-label={`${totalCirculars} notifications`}
                    >
                      {totalCirculars}
                    </span>
                  </div>
                  <div style={{ padding: "14px" }}>
                    {circulars.length > 0 ? (
                      circulars.slice(0, 3).map((item) => (
                        <div
                          key={`${item.entry_type}-${item.id}`}
                          style={{
                            ...softCardStyle,
                            padding: "16px 18px",
                            marginBottom: "12px",
                          }}
                        >
                          <div className="d-flex justify-content-between gap-3 align-items-start">
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                maxWidth: "100%",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              <div
                                style={{
                                  color: "#1677ff",
                                  fontWeight: "800",
                                  marginBottom: "6px",
                                }}
                              >
                                {item.date || "-"}
                              </div>
                              <div
                                style={{
                                  color: "#334155",
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                }}
                              >
                                From:{" "}
                                {item.initiated_by || item.entry_type || "-"}
                              </div>
                              <div
                                style={{ color: "#475569", fontSize: "14px" }}
                              >
                                {item.details || "-"}
                              </div>
                              {item.attachment_url ? (
                                <a
                                  href={item.attachment_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "inline-block",
                                    marginTop: "10px",
                                    color: "#1677ff",
                                    fontWeight: "600",
                                    textDecoration: "none",
                                  }}
                                >
                                  View Attachment
                                </a>
                              ) : null}
                            </div>
                            <span
                              style={{
                                flexShrink: 0,
                                background: item.is_active === false ? "#dc3545" : "#198754",
                                color: "#ffffff",
                                borderRadius: "8px",
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontWeight: "700",
                              }}
                            >
                              {item.is_active === false ? "Inactive" : "Active"}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          ...emptyBoxStyle,
                          minHeight: "180px",
                          color: "#e96d7b",
                        }}
                      >
                        No Record Found!
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              <Col lg={6}>
                <div style={panelStyle}>
                  <Link
                    to="/student/view-result"
                    style={{
                      ...panelHeaderStyle,
                      textDecoration: "none",
                    }}
                  >
                    <span className="d-flex align-items-center gap-2">
                      <FaTrophy />
                      Exam Results
                    </span>
                  </Link>
                  <div style={{ padding: "14px" }}>
                    {results.length > 0 ? (
                      results.slice(0, 3).map((item, index) => (
                        <Link
                          key={item.result_id || `result-${index}`}
                          to="/student/view-result"
                          style={{
                            ...softCardStyle,
                            padding: "16px 18px",
                            marginBottom: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "14px",
                              background: "#fff3d7",
                              color: "#f59e0b",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "22px",
                              flexShrink: 0,
                            }}
                          >
                            <FaGraduationCap />
                          </div>
                          <div>
                            <div
                              style={{ color: "#1677ff", fontWeight: "800" }}
                            >
                              {item.session ||
                                profile?.academic_year ||
                                "Current Session"}
                            </div>
                            <div
                              style={{
                                color: "#475569",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            >
                              {item.result_id
                                ? `${item.course || "-"} / ${item.section || "-"} academic performance`
                                : "Result data is not published yet for this student."}
                            </div>
                            {item.overall_grade ? (
                              <div
                                style={{
                                  color: "#0f172a",
                                  fontSize: "14px",
                                  marginTop: "6px",
                                }}
                              >
                                Overall Grade:{" "}
                                <strong>{item.overall_grade}</strong>
                              </div>
                            ) : null}
                          </div>
                        </Link>
                      ))
                    ) : (
                      <Link
                        to="/student/view-result"
                        style={{
                          ...emptyBoxStyle,
                          minHeight: "180px",
                          flexDirection: "column",
                          gap: "8px",
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        <div style={{ fontSize: "42px", opacity: 0.35 }}>
                          🏆
                        </div>
                        <div>No exam results available</div>
                      </Link>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}

export default Dashboards;
